import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MountainSnow, ArrowRight, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import PartnerLoginModal from "@/components/PartnerLoginModal";

export default function NavbarHeader() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAdvantageActive = location.pathname === "/advantage";

  return (
    <>
      <PartnerLoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      <header className="border-b border-[#E7E2D6] bg-[#F5F3EE]/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" onClick={() => setMobileMenuOpen(false)}>
            <div className="w-10 h-10 rounded-md bg-[#0A1128] flex items-center justify-center group-hover:bg-[#152243] transition-colors">
              <MountainSnow className="w-5 h-5 text-[#C5A880]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif-display text-lg leading-none text-[#0A1128]">Alpine Straits</div>
              <div className="label-eyebrow text-[10px] mt-1">Property Partners</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 text-sm">
            <Link
              to="/launches"
              data-testid="nav-new-launches"
              className="text-[#C5A880] font-semibold hover:text-[#0A1128] transition-colors flex items-center gap-1"
            >
              New Launches
            </Link>
            <Link
              to="/advantage"
              data-testid="nav-advantage"
              className={`transition-colors font-medium flex items-center gap-1.5 ${
                isAdvantageActive
                  ? "text-[#0A1128] font-semibold border-b-2 border-[#C5A880] pb-0.5"
                  : "text-slate-600 hover:text-[#0A1128]"
              }`}
            >
              Advantage
              <span className="text-[9px] uppercase tracking-wider bg-[#0A1128] text-[#C5A880] px-1.5 py-0.5 rounded font-mono font-semibold">
                0% ABSD
              </span>
            </Link>
            {[
              ["About", "/#about"],
              ["Services", "/#services"],
              ["Founders", "/#founders"],
              ["Insights", "/#insights"],
              ["FAQ", "/#faq"],
              ["Contact", "/#contact"],
            ].map(([label, href]) => (
              <a
                key={label}
                href={href}
                className="text-slate-600 hover:text-[#0A1128] transition-colors"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              data-testid="landing-signin-btn"
              onClick={() => {
                setLoginModalOpen(true);
                setMobileMenuOpen(false);
              }}
              className="bg-[#0A1128] text-white hover:bg-[#152243] rounded-md text-xs px-3 sm:px-4"
            >
              Partner sign in <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
            </Button>

            {/* Mobile Hamburger Menu Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-[#0A1128] hover:bg-[#E7E2D6]/60 transition-colors focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E7E2D6] bg-[#F5F3EE] px-6 py-5 space-y-3 shadow-xl animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col space-y-2.5">
              <Link
                to="/launches"
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#C5A880] font-semibold text-base py-2 border-b border-[#E7E2D6]/60 flex items-center justify-between"
              >
                New Launches
                <span className="text-[10px] uppercase font-bold tracking-wider bg-[#0A1128] text-[#C5A880] px-2 py-0.5 rounded">
                  Explore
                </span>
              </Link>
              <Link
                to="/advantage"
                onClick={() => setMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#0A1128] font-medium text-sm py-2 border-b border-[#E7E2D6]/40 transition-colors flex items-center justify-between"
              >
                <span>Advantage</span>
                <span className="text-[10px] uppercase font-semibold text-[#C5A880] bg-[#0A1128] px-2 py-0.5 rounded">
                  0% ABSD
                </span>
              </Link>
              {[
                ["About", "/#about"],
                ["Services", "/#services"],
                ["Founders", "/#founders"],
                ["Insights", "/#insights"],
                ["FAQ", "/#faq"],
                ["Contact", "/#contact"],
              ].map(([label, href]) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-700 hover:text-[#0A1128] font-medium text-sm py-2 border-b border-[#E7E2D6]/40 transition-colors"
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
